import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Minus, Plus, Sparkles, ZoomIn, ZoomOut, Expand } from "lucide-react";
import { Link } from "react-router-dom";
import { PdfPage } from "@/components/PdfPage";
import { ListenToggle, ListenBar } from "@/components/ListenAloud";
import { useLang } from "@/context/LangContext";

const EndInvite = ({ onClose }) => {
  const { t } = useLang();
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="mt-6 bg-[#1C1917] text-[#FAF7F2] rounded-sm p-6 sm:p-8 text-center" data-testid="sample-end-invite">
      <Sparkles className="w-7 h-7 mx-auto text-amber-300" />
      <h3 className="mt-3 font-display text-2xl sm:text-3xl font-bold">{t.samples.endTitle}</h3>
      <p className="mt-3 text-sm sm:text-base text-stone-300 max-w-md mx-auto leading-relaxed">{t.samples.endText}</p>
      <Link to="/register" onClick={onClose} data-testid="sample-end-register-btn"
        className="mt-6 inline-flex items-center justify-center rounded-full bg-amber-400 text-[#1C1917] px-6 py-3 font-bold hover:bg-amber-300 transition-colors">
        {t.samples.endCta}
      </Link>
    </motion.div>
  );
};

export const SamplePreview = ({ sample, onClose, initialListen = false }) => {
  const { t, isRtl } = useLang();
  const [page, setPage] = useState(0);
  const [listen, setListen] = useState(initialListen);
  const [total, setTotal] = useState(sample?.page_count ?? 1);
  const [zoom, setZoom] = useState(() => Number(localStorage.getItem("yh_sample_zoom")) || 1);
  const [error, setError] = useState("");
  const isPhone = window.innerWidth < 768;
  // Every sample opens full screen with the page fitted to the screen; the reader can still switch it off.
  const [big, setBig] = useState(true);
  const fitRef = useRef(null);
  const scale = big && !isPhone ? 1.6 : 1;
  const autoFit = useRef(big);
  const fit = (s = scale) => { if (fitRef.current) setZoom(Math.min(1.6, fitRef.current() / s)); };
  // Full screen: the page fills the screen as soon as it is shown, and again when full screen is switched on.
  const onPageReady = () => {
    if (!autoFit.current) return;
    autoFit.current = false;
    requestAnimationFrame(() => fit());
  };
  const toggleBig = () => {
    const next = !big;
    setBig(next);
    if (next) { autoFit.current = true; setTimeout(() => { if (autoFit.current) { autoFit.current = false; fit(isPhone ? 1 : 1.6); } }, 350); }
  };

  useEffect(() => {
    localStorage.setItem("yh_sample_zoom", String(zoom));
  }, [zoom]);
  const PrevIcon = isRtl ? ChevronRight : ChevronLeft;
  const NextIcon = isRtl ? ChevronLeft : ChevronRight;

  useEffect(() => {
    window.__lenis?.stop();
    return () => { window.__lenis?.start(); };
  }, []);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`fixed inset-0 z-[70] bg-[#1C1917]/80 flex items-center justify-center ${big ? "p-0" : "p-3 sm:p-6"}`} data-testid="sample-preview-overlay">
      <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.6 }}
        className={`w-full flex flex-col overflow-hidden shadow-2xl bg-white text-[#1C1917] ${big ? "h-full max-w-none" : "max-w-3xl max-h-[92vh] rounded-sm"}`} data-testid="sample-preview-window">
        <div className="flex items-center justify-between gap-x-3 gap-y-2 px-4 sm:px-6 py-3 border-b border-black/10 flex-wrap">
          <div className="min-w-0 flex-1 basis-40 order-1">
            <p className="text-xs font-bold text-[#B45309]">{t.samples.eyebrow}</p>
            <h2 className="font-semibold truncate" dir="auto" data-testid="sample-preview-title">{sample.title}</h2>
          </div>
          <button onClick={onClose} className="order-2 lg:order-3 shrink-0 p-2 rounded-full bg-black/5 hover:bg-black/10" aria-label={t.reader.close} data-testid="sample-preview-close"><X className="w-6 h-6" /></button>
          <div className="order-3 lg:order-2 w-full lg:w-auto flex items-center gap-1 flex-wrap justify-start lg:justify-end">
            <button onClick={toggleBig} data-testid="sample-magnify-toggle" aria-pressed={big}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${big ? "bg-[#1C1917] text-white" : "border border-current hover:bg-black/10"}`}>
              {big ? <ZoomOut className="w-4 h-4" /> : <ZoomIn className="w-4 h-4" />} {big ? t.reader.unmagnify : t.reader.magnify}
            </button>
            <ListenToggle on={listen} onChange={setListen} />
            <button onClick={() => fit()} data-testid="sample-fit-btn" aria-label={t.reader.fit} title={t.reader.fit}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-current hover:bg-black/10 transition-colors">
              <Expand className="w-4 h-4" /> <span className="hidden sm:inline">{t.reader.fit}</span>
            </button>
            <span className="inline-flex items-center gap-1 shrink-0">
            <button onClick={() => setZoom((z) => Math.max(0.3, z - 0.1))} className="p-1.5 rounded-full hover:bg-black/10" aria-label="-" data-testid="sample-zoom-out"><Minus className="w-4 h-4" /></button>
            <button onClick={() => setZoom((z) => Math.min(1.6, z + 0.1))} className="p-1.5 rounded-full hover:bg-black/10" aria-label="+" data-testid="sample-zoom-in"><Plus className="w-4 h-4" /></button>
            </span>
          </div>
        </div>
        {listen && <ListenBar src={`/samples/${sample.id}/audio/${page}`} />}
        <div className={`flex-1 overflow-y-auto px-3 sm:px-6 ${big ? "py-2" : "py-6"}`} data-lenis-prevent>
          {error ? <p className="text-center py-10" data-testid="sample-preview-error">{t.reader.pdfError}</p>
            : <PdfPage filePath={`/samples/${sample.id}/file`} page={page} zoom={zoom * scale} onError={setError} onLoad={setTotal} onFitReady={(fn) => { fitRef.current = fn; }} onPageReady={onPageReady} />}
          {page >= total - 1 && <EndInvite onClose={onClose} />}
        </div>
        <div className="px-4 sm:px-6 py-4 border-t border-black/10 flex items-center justify-between gap-3 text-sm">
          <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="sample-prev-btn" className="inline-flex items-center gap-1 px-3 py-2 rounded-full hover:bg-black/10 disabled:opacity-30"><PrevIcon className="w-4 h-4" /> {t.reader.prev}</button>
          <span data-testid="sample-page-indicator">{page + 1} / {total}</span>
          <button disabled={page >= total - 1} onClick={() => setPage((p) => p + 1)} data-testid="sample-next-btn" className="inline-flex items-center gap-1 px-3 py-2 rounded-full hover:bg-black/10 disabled:opacity-30">{t.reader.next} <NextIcon className="w-4 h-4" /></button>
        </div>
        <Link to="/register" onClick={onClose} className="block text-center bg-[#1C1917] text-[#FAF7F2] py-3 text-sm font-semibold hover:bg-[#8B3A2B] transition-colors" data-testid="sample-preview-register">{t.samples.full}</Link>
      </motion.div>
    </motion.div>
  );
};
